import '../styles/adminResponsive.css';
import { usePlan } from '../components/PlanBadges';
import { isProPlan, planLabel } from '../lib/plans';
import ProfileConflict from '../components/ProfileConflict';
import { mergeProfileDraft, profileError } from '../lib/profileDraft';
import { apiErrorMessage } from '../lib/apiError';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Modal from '../components/Modal';
import AdminMemberDetails, { AdminMemberDeleteDialog } from '../components/AdminMemberDetails';
import AdminDirectorySearch, { filterAdminDirectory } from '../components/AdminDirectorySearch';
import { Plus, Crown } from 'lucide-react';
import { AdminPlanControl } from '../components/AdminPlanControl';
import { MultiSelect, BoolSwitch, TextField } from '../components/FormFields';
import { TECHNICAL, DISTRICTS, BUDGET_MODELS } from '../constants/formOptions';
import { EVENT_TYPES_CANON, DAYS_CANON, TIME_RANGES_CANON, BUDGET_MODELS_CANON } from '../constants/canonicalEnums';

// A11: panel formu organizatörün kendi profil formuyla aynı kanonik DNA alanlarını yazar (onay bunları okur).
// Eski panel alanları (event_type, expected_attendees, preferred_*) DNA sayılmaz; eski kayıtta kanonik alan boşsa
// form bu değerleri öneri olarak doldurur, personel kontrol edip kaydeder.
export const ORGANIZER_DISTRICT_OPTIONS = DISTRICTS.map(d => d === 'Kâğıthane' ? { value: 'Kağıthane', label: d } : d);
const DISTRICT_VALUES = ORGANIZER_DISTRICT_OPTIONS.map(d => typeof d === 'object' ? d.value : d);
export const DESCRIPTION_RULE = 'Onay için en az 4 kelime ve 20 karakter.';
const fold = v => String(v ?? '').toLocaleLowerCase('tr').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ı/g, 'i').trim();
const EVENT_ALIASES = { workshop: 'atolye', atelye: 'atolye', seminer: 'konferans', panel: 'konferans', lansman: 'tanitim', standup: 'stand_up', 'stand up': 'stand_up' };
const canon = (options, raw, aliases = {}) => {
  const key = fold(raw);
  const hit = options.find(o => o.value === raw || fold(o.label) === key || fold(o.value) === key);
  return hit ? hit.value : aliases[key] || null;
};
const canonList = (options, values, aliases) => [...new Set((values || []).map(v => canon(options, v, aliases)).filter(Boolean))];
const legacySlot = range => {  // "09:00-17:00" → başlangıç saatine göre dilim (sunucudaki _normalize_time_range ile aynı)
  const hour = parseInt(String(range).match(/\d+/)?.[0], 10);
  if (Number.isNaN(hour)) return canon(TIME_RANGES_CANON, String(range).split(' ')[0]);
  return hour >= 5 && hour < 12 ? 'sabah' : hour >= 12 && hour < 18 ? 'ogleden_sonra' : hour >= 18 && hour < 22 ? 'aksam' : 'gece';
};
export const canonicalFromLegacy = o => ({
  discovery_event_types: o?.discovery_event_types?.length ? o.discovery_event_types : canonList(EVENT_TYPES_CANON, o?.event_type ? [o.event_type] : [], EVENT_ALIASES),
  discovery_districts: o?.discovery_districts?.length ? o.discovery_districts
    : [...new Set((o?.preferred_districts || []).map(d => d === 'Kâğıthane' ? 'Kağıthane' : d).filter(d => DISTRICT_VALUES.includes(d)))],
  discovery_days: o?.discovery_days?.length ? o.discovery_days : canonList(DAYS_CANON, o?.preferred_days),
  discovery_time_ranges: o?.discovery_time_ranges?.length ? o.discovery_time_ranges : [...new Set((o?.preferred_time_ranges || []).map(legacySlot).filter(Boolean))],
  discovery_budget_models: o?.discovery_budget_models?.length ? o.discovery_budget_models : canonList(BUDGET_MODELS_CANON, o?.venue_budget_model ? [o.venue_budget_model] : []),
  typical_attendees_max: String(o?.typical_attendees_max ?? (o?.expected_attendees > 0 ? o.expected_attendees : '')),
});
const CANONICAL_KEYS = ['discovery_event_types', 'discovery_districts', 'discovery_days', 'discovery_time_ranges', 'discovery_budget_models', 'typical_attendees_max'];
export const usesLegacySuggestion = o => {
  const next = canonicalFromLegacy(o);
  return CANONICAL_KEYS.some(k => k === 'typical_attendees_max' ? o?.typical_attendees_max == null && next[k] !== '' : !(o?.[k]?.length) && next[k].length > 0);
};
const labelsOf = (options, values) => (values || []).map(v => (options.find(o => o.value === v) || {}).label || v);
const attendeesOrNull = v => (v === '' || v == null ? null : Number(v));

const emptyForm = {
  name: '', description: '',
  discovery_visible: true,
  discovery_event_types: [], discovery_districts: [], discovery_days: [], discovery_time_ranges: [], discovery_budget_models: [],
  typical_attendees_max: '',
  avg_ticket_price: '0',
  technical_needs: [],
  alcohol_needed: false, outside_materials_needed: false, table_setup_needed: false,
  needs_two_person_table_setup: false, min_table_size_expectation: '',
  needs_personal_workspace: false,
  owner_email: '', owner_name: '',
};

const OrganizerFormFields = ({ form, setForm, t = 'co' }) => (
  <>
          <BoolSwitch label="Onaylı mekânlar organizatörü keşfedebilsin" value={form.discovery_visible} onChange={(v) => setForm({...form, discovery_visible: v})} testId={`${t}-discovery-visible`} />
          <p className="text-sm">Yeni profiller keşfe açık başlar. Organizatör bu tercihi kendi profilinden değiştirebilir.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Organizatör Adı" value={form.name} onChange={(v) => setForm({...form, name: v})} testId={`${t}-name`} />
            <TextField label="En fazla katılımcı (onay için zorunlu)" type="number" value={form.typical_attendees_max} onChange={(v) => setForm({...form, typical_attendees_max: v})} required={false} testId={`${t}-attendees`} />
            <TextField label="Ortalama Bilet Fiyatı (TL)" type="number" step="0.01" value={form.avg_ticket_price} onChange={(v) => setForm({...form, avg_ticket_price: v})} required={false} testId={`${t}-ticket-price`} />
          </div>
          <MultiSelect label="Düzenlediği etkinlik türleri (onay için zorunlu)" options={EVENT_TYPES_CANON} selected={form.discovery_event_types} onChange={(v) => setForm({...form, discovery_event_types: v})} testId={`${t}-event-types`} />
          <MultiSelect label="Çalıştığı ilçeler (onay için zorunlu)" options={ORGANIZER_DISTRICT_OPTIONS} selected={form.discovery_districts} onChange={(v) => setForm({...form, discovery_districts: v})} testId={`${t}-districts`} />
          <MultiSelect label="Tercih ettiği günler" options={DAYS_CANON} selected={form.discovery_days} onChange={(v) => setForm({...form, discovery_days: v})} testId={`${t}-days`} />
          <MultiSelect label="Tercih ettiği saatler" options={TIME_RANGES_CANON} selected={form.discovery_time_ranges} onChange={(v) => setForm({...form, discovery_time_ranges: v})} testId={`${t}-time-ranges`} />
          <MultiSelect label="Mekân için kabul ettiği ödeme koşulları" options={BUDGET_MODELS_CANON} selected={form.discovery_budget_models} onChange={(v) => setForm({...form, discovery_budget_models: v})} testId={`${t}-budget`} />
          <MultiSelect label="Teknik İhtiyaçlar" options={TECHNICAL} selected={form.technical_needs} onChange={(v) => setForm({...form, technical_needs: v})} testId={`${t}-technical`} />
          <div className="grid md:grid-cols-3 gap-3">
            <BoolSwitch label="Alkol gerekli mi?" value={form.alcohol_needed} onChange={(v) => setForm({...form, alcohol_needed: v})} testId={`${t}-alcohol`} />
            <BoolSwitch label="Dışarıdan malzeme getirilecek mi?" value={form.outside_materials_needed} onChange={(v) => setForm({...form, outside_materials_needed: v})} testId={`${t}-outside`} />
            <BoolSwitch label="Masa düzeni gerekli mi?" value={form.table_setup_needed} onChange={(v) => setForm({...form, table_setup_needed: v})} testId={`${t}-table`} />
          </div>
          <div className="border-t border-border pt-4 space-y-3">
            <p className="overline">Masa Düzeni Detayları</p>
            <div className="grid md:grid-cols-2 gap-3">
              <BoolSwitch label="2 kişilik masa düzeni gerekiyor mu?" value={form.needs_two_person_table_setup} onChange={(v) => setForm({...form, needs_two_person_table_setup: v})} testId={`${t}-two-person-table`} />
              <BoolSwitch label="Kişi başı çalışma alanı gerekiyor mu?" value={form.needs_personal_workspace} onChange={(v) => setForm({...form, needs_personal_workspace: v})} testId={`${t}-personal-workspace`} />
              <TextField label="Minimum masa ölçüsü beklentisi" value={form.min_table_size_expectation} onChange={(v) => setForm({...form, min_table_size_expectation: v})} placeholder="örn: 60x60, 80x80" required={false} testId={`${t}-min-table-size`} />
            </div>
          </div>
          <div>
            <label className="overline block mb-2" htmlFor={`${t}-description`}>Açıklama (tanıtım)</label>
            <textarea id={`${t}-description`} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} aria-describedby={`${t}-description-rule`} className="w-full px-4 py-3 border border-border min-h-20 focus:ring-2 focus:ring-black focus:border-black" data-testid={`${t}-description`} />
            <p id={`${t}-description-rule`} className="text-xs text-zinc-500 mt-1">{DESCRIPTION_RULE}</p>
          </div>
  </>
);

const budgetLabel = (val) => (BUDGET_MODELS.find(b => b.value === val) || {}).label || val;

const adminOrganizerFormValues = o => ({
      ...emptyForm, ...o,
      ...canonicalFromLegacy(o),
      avg_ticket_price: String(o.avg_ticket_price ?? 0),
      min_table_size_expectation: o.min_table_size_expectation || '',
      technical_needs: o.technical_needs || [],
    });

const AdminOrganizers = () => {
  const adminPlan = usePlan(0);
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // organizer being edited
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);
  const [conflict, setConflict] = useState(false);

  const openEdit = (o) => {
    setEditing(o);
    setEditForm(adminOrganizerFormValues(o));
    setConflict(false);
  };

  const reloadDraft = async () => {
    setEditSaving(true);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/organizers`);
      const fresh = data.find(o => o.id === editing.id);
      if (!fresh) throw new Error('Profil bulunamadı');
      setEditForm(old => mergeProfileDraft(adminOrganizerFormValues(editing), old, adminOrganizerFormValues(fresh)));
      setEditing(fresh); setOrganizers(data); setConflict(false);
      toast.info('Güncel bilgiler getirildi; değişikliklerini kontrol edip kaydet.');
    } catch (err) { toast.error('Güncel bilgiler alınamadı; yazdıkların korundu.'); }
    finally { setEditSaving(false); }
  };

  const saveEdit = async (approveAfter) => {
    if (!editing) return;
    setEditSaving(true);
    try {
      const { owner_email, owner_name, owner_can_delete, id, user_id, approved, profile_completed, created_at, updated_at, ...rest } = editForm;
      const payload = {
        ...rest,
        profile_revision: editing.profile_revision ?? 0,
        typical_attendees_max: attendeesOrNull(editForm.typical_attendees_max),
        avg_ticket_price: parseFloat(editForm.avg_ticket_price || 0),
        min_table_size_expectation: editForm.min_table_size_expectation || null,
      };
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organizers/${editing.id}`, payload);
      if (approveAfter) {
        try {
          await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/organizers/${editing.id}/approve`, { approve: true });
        } catch (approveErr) {
          // Kayıt yapıldı ama onay olmadı (ör. tanıtım çok kısa): nedeni göster, formu açık bırak.
          const fresh = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/organizers`).then(r => r.data).catch(() => null);
          const updated = fresh && fresh.find(x => x.id === editing.id);
          if (updated) { setOrganizers(fresh); setEditing(updated); }
          toast.error('Bilgiler kaydedildi ama onaylanamadı: ' + apiErrorMessage(approveErr, 'Bir hata oluştu'));
          return;
        }
        toast.success('Profil kaydedildi ve organizatör onaylandı');
      } else {
        toast.success('Profil güncellendi');
      }
      setEditing(null);
      fetchOrganizers();
    } catch (err) {
      if (err.response?.status === 409) setConflict(true);
      toast.error(profileError(err));
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => { fetchOrganizers(); }, []);

  const fetchOrganizers = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/organizers`);
      setOrganizers(data);
    } catch (err) { toast.error('Organizatör listesi yenilenemedi. Lütfen yeniden deneyin.'); }
    finally { setLoading(false); }
  };

  const handleMemberDeleted = userId => {
    setSelected(null);
    setDeleteUserId(null);
    setOrganizers(items => items.filter(item => item.user_id !== userId));
    fetchOrganizers();
  };

  const handleApproval = async (id, approve) => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/organizers/${id}/approve`, { approve });
      toast.success(approve ? 'Organizatör onaylandı' : 'Organizatör reddedildi');
      fetchOrganizers();
      setSelected(null);
    } catch (err) { toast.error(apiErrorMessage(err, 'Bir hata oluştu')); }  // A5: eksik DNA 422 açıklanır
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        approve: true,  // A11: düğme "Oluştur ve Onayla" — onay açıkça istenir; çekirdek DNA eksikse hiçbir kayıt açılmaz
        typical_attendees_max: attendeesOrNull(form.typical_attendees_max),
        avg_ticket_price: parseFloat(form.avg_ticket_price || 0),
        min_table_size_expectation: form.min_table_size_expectation || null,
        owner_email: form.owner_email || null,
        owner_name: form.owner_name || null,
      };
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/organizers`, payload);
      toast.success(data?.approved ? 'Organizatör oluşturuldu ve onaylandı' : 'Organizatör oluşturuldu; onay bekliyor');
      setCreateOpen(false);
      setForm(emptyForm);
      fetchOrganizers();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Bir hata oluştu'));
    }
  };

  if (loading) return <div className="p-8">Yükleniyor...</div>;
  const filteredOrganizers = filterAdminDirectory(organizers, search);

  return (
    <div className="p-8 max-w-7xl mx-auto admin-directory-page">
      <div className="mb-12 flex items-start justify-between flex-wrap gap-4 admin-directory-page-heading">
        <div>
          <p className="overline mb-2">Admin Onayı</p>
          <h1 className="text-3xl font-semibold tracking-tight" data-testid="admin-organizers-title">Organizatörler</h1>
          <p className="text-sm text-muted-foreground mt-2">{organizers.length} organizatör kayıtlı</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="px-6 py-3 btn-primary font-medium uppercase tracking-[0.1em] flex items-center gap-2" data-testid="admin-new-organizer-button">
          <Plus size={16} /> Yeni Organizatör Ekle
        </button>
      </div>

      <AdminDirectorySearch value={search} onChange={setSearch} count={filteredOrganizers.length}
        total={organizers.length} testId="admin-organizers-search" />

      {filteredOrganizers.length === 0 ? (
        <div className="border border-border p-12 text-center"><p>{search.trim() ? 'Aramanızla eşleşen organizatör bulunamadı.' : 'Henüz organizatör kaydı bulunmuyor'}</p></div>
      ) : (
        <div className="border border-border admin-directory-list" data-testid="admin-organizers-list">
          <div className="grid grid-cols-12 p-4 bg-zinc-50 border-b border-border overline text-xs admin-directory-heading">
            <div className="col-span-3">Organizatör</div>
            <div className="col-span-2">Etkinlik</div>
            <div className="col-span-2">Katılımcı</div>
            <div className="col-span-2">Bütçe</div>
            <div className="col-span-1">Durum</div>
            <div className="col-span-2 text-right">Eylem</div>
          </div>
          {filteredOrganizers.map((o) => (
            <div key={o.id} className="admin-directory-entry">
              <div className="grid grid-cols-12 p-4 border-b border-border items-center hover:bg-zinc-50 transition-colors admin-directory-row admin-organizer-row">
                <div className="col-span-3 min-w-0 pr-3 font-medium flex flex-wrap items-center gap-2 admin-directory-name">
                  <span className="min-w-0 max-w-full break-words [overflow-wrap:anywhere] admin-directory-name-text" title={o.name} data-testid={`admin-organizer-name-${o.id}`}>{o.name}</span>
                  {isProPlan(o.owner_plan) && <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#1E3A8A]" data-testid={`admin-organizer-pro-${o.id}`}><Crown size={10} /> {planLabel(o.owner_plan)}</span>}
                  {o.owner_email && <span className="admin-directory-owner-email">{o.owner_email}</span>}
                </div>
                <div className="col-span-2 text-sm admin-directory-event"><span className="admin-mobile-label">Etkinlik:</span>{labelsOf(EVENT_TYPES_CANON, o.discovery_event_types).join(', ') || o.event_type || '—'}</div>
                <div className="col-span-2 font-mono admin-directory-capacity"><span className="admin-mobile-label">Katılımcı:</span>{o.typical_attendees_max ?? o.expected_attendees ?? '—'}</div>
                <div className="col-span-2 text-xs admin-directory-budget"><span className="admin-mobile-label">Bütçe:</span>{labelsOf(BUDGET_MODELS_CANON, o.discovery_budget_models).join(', ') || budgetLabel(o.venue_budget_model)}</div>
                <div className="col-span-1 admin-directory-status">
                  <span className="admin-mobile-label">Durum:</span><span className={o.approved ? 'status-approved' : 'status-pending'} data-testid={`admin-organizer-status-${o.id}`}>
                    {o.profile_completed === false ? 'Profil Eksik' : (o.approved ? 'Onaylı' : 'Beklemede')}
                  </span>{o.pending_revision && <span className="status-pill ml-1" data-testid={`admin-organizer-review-${o.id}`}>İncelemede</span>}
                </div>
                <div className="col-span-2 text-right admin-directory-actions">
                  <button onClick={() => setSelected(selected?.id === o.id ? null : o)} className="text-xs uppercase tracking-[0.1em] font-medium hover:text-[#1E3A8A] transition-colors" data-testid={`admin-organizer-toggle-${o.id}`}>
                    {selected?.id === o.id ? 'Kapat' : 'Detay'} →
                  </button>
                  {o.owner_can_delete === true && <button type="button" onClick={() => setDeleteUserId(o.user_id)}
                    className="admin-member-button admin-member-danger" data-testid={`admin-organizer-delete-member-${o.id}`}
                    aria-label={`${o.name}: Üyeliği sil`}>Üyeliği sil</button>}
                </div>
              </div>
              {selected?.id === o.id && (
                <>
                  <div className="px-8 pt-6 bg-zinc-50 flex items-center gap-3 flex-wrap admin-directory-plan">
                    <p className="overline">Üyelik Planı</p>
                    <AdminPlanControl canManageProPlus={adminPlan.can_manage_pro_plus === true} userId={o.user_id} plan={o.owner_plan} storedPlan={o.owner_plan_stored} expiresAt={o.owner_plan_expires_at}
                      tier={o.owner_tier} testId={`admin-organizer-plan-${o.id}`}
                      onChanged={(patch) => setOrganizers(prev => prev.map(x => x.id === o.id ? { ...x, ...patch } : x))} />
                  </div>
                  <AdminMemberDetails userId={o.user_id} onDeleted={handleMemberDeleted} />
                  <OrganizerDetail organizer={o} onApprove={(a) => handleApproval(o.id, a)} onEdit={() => openEdit(o)} />
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {deleteUserId && <AdminMemberDeleteDialog key={deleteUserId} userId={deleteUserId}
        onClose={() => setDeleteUserId(null)} onDeleted={handleMemberDeleted} />}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Yeni Organizatör Ekle" testId="create-organizer-modal">
        <form onSubmit={handleCreate} className="space-y-6" data-testid="create-organizer-form">
          <OrganizerFormFields form={form} setForm={setForm} t="co" />
          <div className="border-t border-border pt-4">
            <p className="overline mb-3">Organizatör Sahibi (İsteğe Bağlı)</p>
            <div className="grid md:grid-cols-2 gap-4">
              <TextField label="Sahip E-posta" value={form.owner_email} onChange={(v) => setForm({...form, owner_email: v})} placeholder="Boş bırakırsanız sahipsiz" required={false} testId="co-owner-email" />
              <TextField label="Sahip İsim" value={form.owner_name} onChange={(v) => setForm({...form, owner_name: v})} required={false} testId="co-owner-name" />
            </div>
          </div>
          <div className="flex gap-3 pt-4 border-t border-border">
            <button type="submit" className="px-6 py-3 btn-primary font-medium uppercase tracking-[0.1em]" data-testid="co-submit">Oluştur ve Onayla →</button>
            <button type="button" onClick={() => setCreateOpen(false)} className="px-6 py-3 border border-border font-medium uppercase tracking-[0.1em] hover:bg-zinc-50">İptal</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!editing} onClose={() => { if (!editSaving) setEditing(null); }} title="Organizatör Profilini Düzenle" testId="edit-organizer-modal">
        {editing && (
          <form onSubmit={(e) => { e.preventDefault(); saveEdit(false); }} className="space-y-6" data-testid="edit-organizer-form">
            <p className="text-xs text-zinc-500">Aynı organizatör kaydı ({editing.name}) güncellenir; onay durumu bu formla değişmez.</p>
            {usesLegacySuggestion(editing) && <p className="home-notice text-sm" role="status" data-testid="eo-legacy-notice">Etkinlik türü, ilçe, gün/saat ve katılımcı bilgileri eski panel kaydından önerildi. Kontrol edip kaydedin; onay bu alanları okur.</p>}
            <ProfileConflict visible={conflict} busy={editSaving} onReload={reloadDraft} />
          <fieldset disabled={editSaving}><OrganizerFormFields form={editForm} setForm={setEditForm} t="eo" /></fieldset>
            <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
              <button type="submit" disabled={editSaving} className="px-6 py-3 btn-primary font-medium uppercase tracking-[0.1em] disabled:opacity-50" data-testid="eo-submit">{editSaving ? 'Kaydediliyor…' : 'Kaydet'}</button>
              {!editing.approved && (
                <button type="button" disabled={editSaving} onClick={() => saveEdit(true)} className="px-6 py-3 border border-[#1E3A8A] text-[#1E3A8A] font-medium uppercase tracking-[0.1em] hover:bg-[#E6EEFF] disabled:opacity-50" data-testid="eo-save-approve">Kaydet ve Onayla →</button>
              )}
              <button type="button" disabled={editSaving} onClick={() => setEditing(null)} className="px-6 py-3 border border-border font-medium uppercase tracking-[0.1em] hover:bg-zinc-50">İptal</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

const OrganizerDetail = ({ organizer, onApprove, onEdit }) => (
  <div className="p-8 bg-zinc-50 border-b border-border admin-directory-detail">
    <div className="grid md:grid-cols-2 gap-8 mb-6">
      <DetailItem label="Düzenlediği etkinlik türleri" chips={labelsOf(EVENT_TYPES_CANON, organizer.discovery_event_types)} />
      <DetailItem label="Çalıştığı ilçeler" chips={organizer.discovery_districts?.length ? organizer.discovery_districts : organizer.preferred_districts} />
      <DetailItem label="Tercih ettiği günler" chips={organizer.discovery_days?.length ? labelsOf(DAYS_CANON, organizer.discovery_days) : organizer.preferred_days} />
      <DetailItem label="Tercih ettiği saatler" chips={organizer.discovery_time_ranges?.length ? labelsOf(TIME_RANGES_CANON, organizer.discovery_time_ranges) : organizer.preferred_time_ranges} mono />
      <DetailItem label="En fazla katılımcı" value={organizer.typical_attendees_max ? String(organizer.typical_attendees_max) : ''} mono />
      <DetailItem label="Teknik İhtiyaçlar" chips={organizer.technical_needs} />
      <DetailItem label="Ortalama Bilet Fiyatı" value={`${organizer.avg_ticket_price || 0} TL`} mono />
      <DetailItem label="Ödeme koşulları" value={labelsOf(BUDGET_MODELS_CANON, organizer.discovery_budget_models).join(', ') || budgetLabel(organizer.venue_budget_model)} />
      <DetailItem label="Alkol / Dışarı / Masa" value={`${organizer.alcohol_needed ? 'Gerekli' : 'Gerekli değil'} • ${organizer.outside_materials_needed ? 'Gerekli' : 'Gerekli değil'} • ${organizer.table_setup_needed ? 'Gerekli' : 'Gerekli değil'}`} />
      <DetailItem label="2 Kişilik Masa / Kişi Başı Çalışma" value={`${organizer.needs_two_person_table_setup ? 'Gerekli' : 'Gerekli değil'} • ${organizer.needs_personal_workspace ? 'Gerekli' : 'Gerekli değil'}`} />
      <DetailItem label="Min. Masa Ölçüsü Beklentisi" value={organizer.min_table_size_expectation} mono />
      <DetailItem label="Açıklama" value={organizer.description} fullWidth />
    </div>
        <button type="button" onClick={onEdit} className="px-6 py-3 border border-border font-medium uppercase tracking-[0.1em] hover:bg-zinc-50" data-testid={`admin-organizer-edit-${organizer.id}`}>Profili Düzenle</button>
    {!organizer.approved && (
      <div className="flex gap-3 items-center admin-directory-detail-actions">
        <button onClick={() => onApprove(true)} disabled={organizer.profile_completed === false}
          title={organizer.profile_completed === false ? 'Organizatör profilini tamamlamadan onaylanamaz' : undefined}
          className="px-6 py-3 btn-primary font-medium uppercase tracking-[0.1em] disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`admin-organizer-approve-${organizer.id}`}>Onayla →</button>
        {organizer.profile_completed === false && <span className="text-xs text-zinc-500" data-testid={`admin-organizer-incomplete-${organizer.id}`}>Profil eksik — onay için profil tamamlanmalı.</span>}
        <button onClick={() => onApprove(false)} className="px-6 py-3 btn-alert font-medium uppercase tracking-[0.1em]" data-testid={`admin-organizer-reject-${organizer.id}`}>Reddet</button>
      </div>
    )}
  </div>
);

const DetailItem = ({ label, value, chips, mono, fullWidth }) => (
  <div className={fullWidth ? 'md:col-span-2' : ''}>
    <p className="overline mb-2">{label}</p>
    {chips ? (
      chips && chips.length ? (
        <div className="flex flex-wrap gap-2">
          {chips.map((c, i) => <span key={i} className={`${mono ? 'font-mono' : ''} text-xs px-2 py-1 border border-border bg-white`}>{c}</span>)}
        </div>
      ) : <p className="text-sm text-muted-foreground">—</p>
    ) : (
      <p className={`${mono ? 'font-mono' : ''} text-sm`}>{value || '—'}</p>
    )}
  </div>
);

export default AdminOrganizers;

